import React, { createContext, useContext, useEffect, useState } from 'react';
import styled, { ThemeProvider as StyledThemeProvider, createGlobalStyle } from 'styled-components';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Modern theme colors based on ui-sandbox
const lightTheme = {
  // Base colors
  background: 'hsl(0 0% 98%)',
  foreground: 'hsl(168 95% 15%)',
  
  // Card colors
  card: 'hsl(0 0% 100%)',
  cardForeground: 'hsl(168 95% 15%)',
  
  // Popover colors
  popover: 'hsl(0 0% 100%)',
  popoverForeground: 'hsl(168 95% 15%)',
  
  // Primary colors
  primary: 'hsl(104 98% 38%)',
  primaryForeground: 'hsl(0 0% 100%)',
  primaryGlow: 'hsl(78 100% 44%)',
  
  // Secondary colors
  secondary: 'hsl(168 95% 12%)',
  secondaryForeground: 'hsl(0 0% 100%)',
  
  // Muted colors
  muted: 'hsl(168 20% 96%)',
  mutedForeground: 'hsl(168 25% 45%)',
  
  // Accent colors
  accent: 'hsl(78 100% 44%)',
  accentForeground: 'hsl(168 95% 15%)',
  
  // Status colors
  destructive: 'hsl(0 84% 60%)',
  destructiveForeground: 'hsl(0 0% 100%)',
  success: 'hsl(104 98% 38%)',
  successForeground: 'hsl(0 0% 100%)',
  warning: 'hsl(38 92% 60%)',
  warningForeground: 'hsl(0 0% 100%)',
  
  // Border and input colors
  border: 'hsl(168 20% 88%)',
  input: 'hsl(168 20% 92%)',
  ring: 'hsl(104 98% 38%)',
  
  // Brand colors
  brandDark: 'hsl(168 97% 10%)',
  brandGreen: 'hsl(104 98% 38%)',
  brandLime: 'hsl(78 100% 44%)',
  
  // Gradients
  gradientPrimary: 'linear-gradient(135deg, hsl(104 98% 38%), hsl(78 100% 44%))',
  gradientBrand: 'linear-gradient(135deg, hsl(104 98% 38%), hsl(78 100% 44%))',
  gradientSubtle: 'linear-gradient(180deg, hsl(0 0% 98%), hsl(168 20% 96%))',
  
  // Shadows
  shadowElegant: '0 8px 30px -8px hsl(104 98% 38% / 0.15)',
  shadowSoft: '0 4px 20px -4px hsl(168 95% 15% / 0.08)',
  shadowSm: '0 1px 2px 0 hsl(168 95% 15% / 0.05)',
  shadowMd: '0 4px 6px -1px hsl(168 95% 15% / 0.1), 0 2px 4px -1px hsl(168 95% 15% / 0.06)',
  shadowLg: '0 10px 15px -3px hsl(168 95% 15% / 0.1), 0 4px 6px -2px hsl(168 95% 15% / 0.05)',
  
  // Transitions
  transitionSmooth: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  // Border radius
  radius: '0.5rem',
  
  // Typography
  fontSans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontMono: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
  fontLight: '300',
  fontNormal: '400',
  fontMedium: '500',
  fontSemibold: '600',
  fontBold: '700',
  fontExtrabold: '800',
  
  // Font sizes
  textXs: '0.75rem',
  textSm: '0.875rem',
  textBase: '1rem',
  textLg: '1.125rem',
  textXl: '1.25rem',
  text2xl: '1.5rem',
  text3xl: '1.875rem',
  text4xl: '2.25rem',
  
  // Line heights
  leadingNone: '1',
  leadingTight: '1.25',
  leadingSnug: '1.375',
  leadingNormal: '1.5',
  leadingRelaxed: '1.625',
  leadingLoose: '2',
  
  // Spacing
  spacingXs: '0.25rem',
  spacingSm: '0.5rem',
  spacingMd: '1rem',
  spacingLg: '1.5rem',
  spacingXl: '2rem',
  spacing2xl: '3rem',
  
  // Z-index
  zDropdown: '1000',
  zSticky: '1020',
  zFixed: '1030',
  zModalBackdrop: '1040',
  zModal: '1050',
  zPopover: '1060',
  zTooltip: '1070',
  zToast: '1080',
  
  // Sidebar colors
  sidebarBackground: 'hsl(0 0% 98%)',
  sidebarForeground: 'hsl(240 5.3% 26.1%)',
  sidebarPrimary: 'hsl(240 5.9% 10%)',
  sidebarPrimaryForeground: 'hsl(0 0% 98%)',
  sidebarAccent: 'hsl(240 4.8% 95.9%)',
  sidebarAccentForeground: 'hsl(240 5.9% 10%)',
  sidebarBorder: 'hsl(220 13% 91%)',
  sidebarRing: 'hsl(217.2 91.2% 59.8%)',
};

const darkTheme = {
  // Base colors
  background: 'hsl(168 97% 10%)',
  foreground: 'hsl(0 0% 98%)',
  
  // Card colors
  card: 'hsl(168 85% 12%)',
  cardForeground: 'hsl(0 0% 98%)',
  
  // Popover colors
  popover: 'hsl(168 85% 12%)',
  popoverForeground: 'hsl(0 0% 98%)',
  
  // Primary colors
  primary: 'hsl(78 100% 44%)',
  primaryForeground: 'hsl(168 95% 15%)',
  primaryGlow: 'hsl(104 98% 38%)',
  
  // Secondary colors
  secondary: 'hsl(168 70% 15%)',
  secondaryForeground: 'hsl(0 0% 98%)',
  
  // Muted colors
  muted: 'hsl(168 50% 18%)',
  mutedForeground: 'hsl(168 20% 65%)',
  
  // Accent colors
  accent: 'hsl(104 98% 38%)',
  accentForeground: 'hsl(0 0% 98%)',
  
  // Status colors
  destructive: 'hsl(0 62% 45%)',
  destructiveForeground: 'hsl(0 0% 98%)',
  success: 'hsl(104 98% 38%)',
  successForeground: 'hsl(0 0% 98%)',
  warning: 'hsl(38 92% 60%)',
  warningForeground: 'hsl(0 0% 98%)',
  
  // Border and input colors
  border: 'hsl(168 50% 20%)',
  input: 'hsl(168 50% 18%)',
  ring: 'hsl(78 100% 44%)',
  
  // Brand colors
  brandDark: 'hsl(168 97% 10%)',
  brandGreen: 'hsl(104 98% 38%)',
  brandLime: 'hsl(78 100% 44%)',
  
  // Gradients
  gradientPrimary: 'linear-gradient(135deg, hsl(78 100% 44%), hsl(104 98% 38%))',
  gradientBrand: 'linear-gradient(135deg, hsl(78 100% 44%), hsl(104 98% 38%))',
  gradientSubtle: 'linear-gradient(180deg, hsl(168 97% 10%), hsl(168 50% 18%))',
  
  // Shadows
  shadowElegant: '0 8px 30px -8px hsl(78 100% 44% / 0.15)',
  shadowSoft: '0 4px 20px -4px hsl(0 0% 98% / 0.08)',
  shadowSm: '0 1px 2px 0 hsl(0 0% 98% / 0.05)',
  shadowMd: '0 4px 6px -1px hsl(0 0% 98% / 0.1), 0 2px 4px -1px hsl(0 0% 98% / 0.06)',
  shadowLg: '0 10px 15px -3px hsl(0 0% 98% / 0.1), 0 4px 6px -2px hsl(0 0% 98% / 0.05)',
  
  // Transitions
  transitionSmooth: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  // Border radius
  radius: '0.5rem',
  
  // Typography
  fontSans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontMono: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
  fontLight: '300',
  fontNormal: '400',
  fontMedium: '500',
  fontSemibold: '600',
  fontBold: '700',
  fontExtrabold: '800',
  
  // Font sizes
  textXs: '0.75rem',
  textSm: '0.875rem',
  textBase: '1rem',
  textLg: '1.125rem',
  textXl: '1.25rem',
  text2xl: '1.5rem',
  text3xl: '1.875rem',
  text4xl: '2.25rem',
  
  // Line heights
  leadingNone: '1',
  leadingTight: '1.25',
  leadingSnug: '1.375',
  leadingNormal: '1.5',
  leadingRelaxed: '1.625',
  leadingLoose: '2',
  
  // Spacing
  spacingXs: '0.25rem',
  spacingSm: '0.5rem',
  spacingMd: '1rem',
  spacingLg: '1.5rem',
  spacingXl: '2rem',
  spacing2xl: '3rem',
  
  // Z-index
  zDropdown: '1000',
  zSticky: '1020',
  zFixed: '1030',
  zModalBackdrop: '1040',
  zModal: '1050',
  zPopover: '1060',
  zTooltip: '1070',
  zToast: '1080',
  
  // Sidebar colors
  sidebarBackground: 'hsl(168 90% 8%)',
  sidebarForeground: 'hsl(0 0% 95%)',
  sidebarPrimary: 'hsl(104 98% 38%)',
  sidebarPrimaryForeground: 'hsl(0 0% 100%)',
  sidebarAccent: 'hsl(168 70% 15%)',
  sidebarAccentForeground: 'hsl(0 0% 95%)',
  sidebarBorder: 'hsl(168 50% 20%)',
  sidebarRing: 'hsl(78 100% 44%)',
};

// Global styles
const GlobalStyles = createGlobalStyle<{ theme: typeof lightTheme }>`
  * {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: ${props => props.theme.fontSans};
    background-color: ${props => props.theme.background};
    color: ${props => props.theme.foreground};
    transition: ${props => props.theme.transitionSmooth};
  }

  /* Enhanced focus states */
  :focus-visible {
    outline: none;
  }

  /* Selection styling */
  ::selection {
    background-color: ${props => props.theme.primary}33;
    color: ${props => props.theme.foreground};
  }

  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.muted};
    border-radius: ${props => props.theme.radius};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: ${props => props.theme.radius};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.mutedForeground};
  }
`;

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children, 
  defaultTheme = 'dark' 
}) => {
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  const [isDark, setIsDark] = useState(true);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as Theme;
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  // Detect system theme
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        setIsDark(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    
    // Set initial theme
    if (theme === 'system') {
      setIsDark(mediaQuery.matches);
    } else {
      setIsDark(theme === 'dark');
    }

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Update theme in localStorage
  useEffect(() => {
    localStorage.setItem('theme', theme);
  }, [theme]);

  const currentTheme = isDark ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDark }}>
      <StyledThemeProvider theme={currentTheme}>
        <GlobalStyles theme={currentTheme} />
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 